import type { ReactNode } from "react";

import { useOptionalSession } from "@/auth/SessionContext";
import { Button } from "@/ui/Button";
import { DeniedState, ErrorState, UnavailableState } from "@/ui/states";

import { ApiError } from "./client";
import { isTerminalQueryError } from "./queryClient";

/**
 * Renders the right surface state for a failed query.
 *
 * Not every failure is the same kind of failure, and offering the wrong
 * recovery is its own defect:
 *
 *  - **401** means the sign-in itself is gone — expired past silent renewal
 *    or ended elsewhere. The recovery is a new interactive sign-in (#44).
 *  - **403** is a denial. Retrying will not grant access.
 *  - **404** is terminal. Some resource surfaces mask it as a denial; others
 *    retain their failure copy but still offer no retry.
 *  - **501** means the Editor Service has not built the endpoint yet. Issue #13
 *    ships real schemas ahead of its handlers, so the shell will meet this
 *    routinely until those land. A "Try again" button here would be a control
 *    that cannot possibly work — exactly what issue #4 forbids.
 *  - Anything else is treated as transient and does offer a retry.
 *
 * Screens use this rather than branching themselves, so #30–#41 inherit the
 * distinction instead of each rediscovering it.
 */
export function QueryErrorState({
  error,
  onRetry,
  title,
  description,
  deniedDescription,
  subject,
  treatNotFoundAsDenied = false,
}: {
  error: unknown;
  onRetry: () => void;
  /** Heading for the transient-failure case. */
  title: string;
  description?: ReactNode;
  deniedDescription?: ReactNode;
  /** Noun used in the not-implemented heading, e.g. "Projects". */
  subject: string;
  /** Some surfaces cannot distinguish "absent" from "not yours". */
  treatNotFoundAsDenied?: boolean;
}) {
  const session = useOptionalSession();

  if (error instanceof ApiError) {
    if (error.isNotImplemented) {
      return (
        <UnavailableState
          title={`${subject} are not available yet`}
          description="The Editor Service accepts this request but has not implemented it yet, so there is nothing to show. This resolves when the endpoint is deployed — there is nothing to retry."
          blockedBy="Blocked by issue #13 — Editor Service contract and endpoints."
        />
      );
    }

    // Unauthenticated mid-session is expiry, not denial (#44): silent renewal
    // ran out of road and only a new interactive sign-in can help. Offering
    // "ask an administrator" here would be the wrong recovery.
    if (error.isUnauthenticated) {
      return (
        <DeniedState
          title="Your session has expired"
          description="Sign in again to continue. This view reloads with your work after you return."
          {...(session
            ? {
                action: (
                  <Button
                    variant="primary"
                    onClick={() => void session.adapter.signIn()}
                  >
                    Sign in again
                  </Button>
                ),
              }
            : {})}
        />
      );
    }

    if (
      error.isForbidden ||
      (treatNotFoundAsDenied && error.status === 404)
    ) {
      return (
        <DeniedState
          {...(deniedDescription !== undefined
            ? { description: deniedDescription }
            : {})}
        />
      );
    }
  }

  return (
    <ErrorState
      title={title}
      {...(description !== undefined ? { description } : {})}
      {...(!isTerminalQueryError(error) ? { onRetry } : {})}
    />
  );
}
